import React, { useState } from 'react';
import { Grid } from '@mui/material';
import { HospitalBank, Hospital } from 'types/hospital';
import { Bank } from 'types/bank';
import HospitalBanksList from './list/HospitalBanksList';
import HospitalBankForm from './form/HospitalBankForm';
import ConfirmDialog from 'components/confirm-dialog/ConfirmDialog';
import { countLabel, RecordToolbar } from 'components/record-shell/RecordList';

interface HospitalBanksProps {
  hospital: Hospital;
  banks: Bank[];
  handleChangeAddBank(bank: HospitalBank): void;
  handleDeleteBank(bank: HospitalBank): void;
}

const HospitalBanks: React.FC<HospitalBanksProps> = ({ hospital, handleDeleteBank, handleChangeAddBank, banks }) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState<HospitalBank | null>(null);
  const [bankInfo, setBankInfo] = useState<HospitalBank>({
    bank_account: '',
    bank_agency: '',
    bank_name: '',
    hospital_id: hospital.id,
    id: 0,
    pix_key: '',
    pix_key_type: '',
    account_holder: '',
    operation: '',
  });

  function handleChangeBankInfo(index: keyof HospitalBank, value: any) {
    setBankInfo(prev => ({ ...prev, [index]: value }));
  }

  function handleAddBank(bankInfo: HospitalBank | null) {
    if (!bankInfo) return;
    handleChangeAddBank(bankInfo);
    setAddDialogOpen(false);
    setBankInfo({
      bank_account: '',
      bank_agency: '',
      bank_name: '',
      hospital_id: hospital.id,
      id: 0,
      pix_key: '',
      pix_key_type: '',
      account_holder: '',
      operation: '',
    });
  }

  function onExited() {
    setAddDialogOpen(false);
    setBankInfo({
      bank_account: '',
      bank_agency: '',
      bank_name: '',
      hospital_id: hospital.id,
      id: 0,
      pix_key: '',
      pix_key_type: '',
      account_holder: '',
      operation: '',
    });
    setEditDialogOpen(false);
  }

  function handleEditBank(bank: HospitalBank) {
    setEditDialogOpen(true);
    setBankInfo(bank);
  }

  return (
    <Grid container size={12} spacing={2}>
      {confirmDialogOpen && (
        <ConfirmDialog
          confirmLabel="Excluir"
          destructive
          handleNoClick={() => setConfirmDialogOpen(null)}
          handleYesClick={() => {
            handleDeleteBank(confirmDialogOpen);
            setConfirmDialogOpen(null);
          }}
          title="Tem certeza que deseja excluir esta conta bancária?"
        />
      )}
      <Grid size={12}>
        <RecordToolbar
          title={countLabel(hospital.banks.length, 'conta bancária', 'contas bancárias', 'Nenhuma conta bancária')}
          addLabel="Adicionar conta"
          onAdd={() => setAddDialogOpen(true)}
        />
      </Grid>

      {hospital.banks.length > 0 && (
        <Grid size={12}>
          <HospitalBanksList
            banks={hospital.banks}
            handleDeleteBank={item => setConfirmDialogOpen(item)}
            handleEditBank={handleEditBank}
          />
        </Grid>
      )}

      {addDialogOpen && (
        <HospitalBankForm
          type="new"
          handleChangeBankInfo={handleChangeBankInfo}
          bankInfo={bankInfo}
          handleAddBank={handleAddBank}
          onExited={onExited}
          banks={banks}
        />
      )}

      {!!bankInfo.id && editDialogOpen && (
        <HospitalBankForm
          type="edit"
          handleChangeBankInfo={handleChangeBankInfo}
          bankInfo={bankInfo}
          handleAddBank={handleAddBank}
          onExited={onExited}
          banks={banks}
        />
      )}
    </Grid>
  );
};

export default HospitalBanks;
